// src/services/auth.service.js
import apiClient, { saveTokens, clearTokens } from './api.service';

class AuthService {
  
  /**
   * Helper pour vérifier les réponses du serveur
   */
  isSuccessResponse(response) {
    return response.data.success === true || response.data.status === 'success';
  }

  /**
   * Connexion
   */
  async login(email, password) {
    try {
      console.log('🔐 Tentative de connexion:', email);
      
      const response = await apiClient.post('/api/auth/login', {
        email,
        password
      });
      
      console.log('📡 Réponse serveur:', response.data);
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur de connexion');
      }
      
      const { access_token, refresh_token, user } = response.data.data;
      
      // Vérifier le rôle
      if (user.role !== 'driver') {
        throw new Error('Accès réservé aux livreurs uniquement');
      }
      
      // Sauvegarder les tokens
      saveTokens(access_token, refresh_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('✅ Connexion réussie:', user.email);
      return { user, access_token };
      
    } catch (error) {
      console.error('❌ Erreur connexion complète:', error);
      
      // Gestion d'erreur plus robuste
      if (error.response) {
        // Erreur HTTP du serveur
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        // Pas de réponse du serveur
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        // Autre erreur
        throw error;
      }
    }
  }

  /**
   * Inscription (depuis le formulaire e-commerce)
   */
  async register(userData) {
    try {
      console.log('📝 Inscription nouveau livreur:', userData.email);
      
      const response = await apiClient.post('/api/auth/register', {
        ...userData,
        role: 'driver' // Forcer le rôle driver
      });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur lors de l\'inscription');
      }
      
      console.log('✅ Inscription réussie');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur inscription:', error);
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Confirmation d'email
   */
  async confirmEmail(token) {
    try {
      console.log('📧 Confirmation email avec token:', token.substring(0, 10) + '...');
      
      const response = await apiClient.post('/api/auth/confirm-email', { token });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur de confirmation');
      }
      
      console.log('✅ Email confirmé');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur confirmation email:', error);
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Déconnexion
   */
  async logout() {
    try {
      console.log('🚪 Déconnexion...');
      
      const response = await apiClient.post('/api/auth/logout');
      
      // Même si la déconnexion côté serveur échoue, on nettoie côté client
      clearTokens();
      console.log('✅ Déconnexion réussie');
      
    } catch (error) {
      console.error('❌ Erreur déconnexion:', error);
      // Toujours nettoyer les tokens même en cas d'erreur
      clearTokens();
    }
  }

  /**
   * Récupérer l'utilisateur actuel
   */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('❌ Erreur parsing user:', error);
      // Nettoyer les données corrompues
      localStorage.removeItem('user');
      return null;
    }
  }

  /**
   * Vérifier si l'utilisateur est authentifié
   */
  isAuthenticated() {
    const token = localStorage.getItem('access_token');
    const user = this.getCurrentUser();
    return !!(token && user && user.role === 'driver');
  }

  /**
   * Mot de passe oublié
   */
  async forgotPassword(email) {
    try {
      console.log('🔑 Demande de réinitialisation mot de passe:', email);
      
      const response = await apiClient.post('/api/auth/forgot-password', { email });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur lors de la demande');
      }
      
      console.log('✅ Email de réinitialisation envoyé');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur mot de passe oublié:', error);
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Réinitialiser le mot de passe
   */
  async resetPassword(token, newPassword) {
    try {
      console.log('🔄 Réinitialisation mot de passe avec token:', token.substring(0, 10) + '...');
      
      const response = await apiClient.post('/api/auth/reset-password', {
        token,
        new_password: newPassword
      });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur lors de la réinitialisation');
      }
      
      console.log('✅ Mot de passe réinitialisé');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur réinitialisation:', error);
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Changer le mot de passe (utilisateur connecté)
   */
  async changePassword(currentPassword, newPassword) {
    try {
      console.log('🔄 Changement de mot de passe...');
      
      const response = await apiClient.post('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur lors du changement');
      }
      
      console.log('✅ Mot de passe changé');
      return response.data.data;
      
    } catch (error) {
      console.error('❌ Erreur changement mot de passe:', error);
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else if (error.request) {
        throw new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        throw error;
      }
    }
  }

  /**
   * Rafraîchir le token
   */
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (!refreshToken) {
        throw new Error('Aucun token de rafraîchissement trouvé');
      }
      
      console.log('🔄 Rafraîchissement du token...');
      
      const response = await apiClient.post('/api/auth/refresh-token', {
        refresh_token: refreshToken
      });
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur de rafraîchissement');
      }
      
      const { access_token, refresh_token: newRefreshToken } = response.data.data;
      
      // Sauvegarder les nouveaux tokens
      saveTokens(access_token, newRefreshToken);
      
      console.log('✅ Token rafraîchi');
      return access_token;
      
    } catch (error) {
      console.error('❌ Erreur rafraîchissement token:', error);
      
      // En cas d'erreur, déconnecter l'utilisateur
      clearTokens();
      
      if (error.response) {
        const message = error.response.data?.message || `Erreur ${error.response.status}`;
        throw new Error(message);
      } else {
        throw error;
      }
    }
  }

  /**
   * Vérifier le statut du compte
   */
  async checkAccountStatus() {
    try {
      console.log('🔍 Vérification statut du compte...');
      
      const response = await apiClient.get('/api/auth/me');
      
      if (!this.isSuccessResponse(response)) {
        throw new Error(response.data.message || 'Erreur de vérification');
      }
      
      const user = response.data.data;
      
      // Mettre à jour les données utilisateur
      localStorage.setItem('user', JSON.stringify(user));
      
      console.log('✅ Statut du compte vérifié');
      return user;
      
    } catch (error) {
      console.error('❌ Erreur vérification statut:', error);
      
      if (error.response?.status === 401) {
        // Token invalide, déconnecter
        clearTokens();
      }
      
      throw error;
    }
  }

  /**
   * Nettoyer complètement la session
   */
  clearSession() {
    clearTokens();
    localStorage.removeItem('remembered_email');
    console.log('🧹 Session nettoyée');
  }
}

export default new AuthService();